import type { MouseEventHandler } from 'react';
import { forwardRef, Fragment } from 'react';
import cx from 'classnames';
import { Item as RadixDropdownMenuItem } from '@radix-ui/react-dropdown-menu';
import NextLink from 'next/link';

import type { DropdownMenuItemProps } from 'Storybook/peripheral/DropdownMenu/DropdownMenu.types';
import { Variant } from 'Types/variant';
import { applyCypressData } from 'Utilities/helpers';

import styles from './DropdownMenuItem.module.scss';

const Item = forwardRef<HTMLAnchorElement | HTMLButtonElement, DropdownMenuItemProps>(
	(
		{
			customIconProps,
			cypressId,
			href,
			icon: Icon,
			id,
			isAnchorTag = false,
			onClick,
			text,
			variant = Variant.PRIMARY,
			...props
		},
		forwardedRef,
	) => {
		const content = (
			<>
				{Icon && <Icon aria-hidden="true" data-testid={`${id}-icon`} size="small" {...customIconProps} />}
				{text}
			</>
		);

		const classNames = cx(styles.link, styles[variant]);
		const Tag = isAnchorTag ? Fragment : NextLink;

		if (href) {
			const tagProps = isAnchorTag ? {} : { as: props?.as, legacyBehavior: true, passHref: true };

			const anchorProps = {
				...applyCypressData(cypressId),
				className: classNames,
				'data-thc-style-override': 'true',
				onClick,
				ref: forwardedRef as React.Ref<HTMLAnchorElement>,
				...(isAnchorTag && { href: href.toString() }),
				...props,
			};

			return (
				<Tag href={href} {...tagProps}>
					<a {...anchorProps}>{content}</a>
				</Tag>
			);
		}

		return (
			<button
				{...applyCypressData(cypressId)}
				ref={forwardedRef as React.Ref<HTMLButtonElement>}
				className={classNames}
				onClick={onClick as MouseEventHandler<HTMLButtonElement>}
				{...props}
			>
				{content}
			</button>
		);
	},
);

const DropdownMenuItem: React.FC<DropdownMenuItemProps> = (props) => (
	<RadixDropdownMenuItem asChild>
		<Item {...props} />
	</RadixDropdownMenuItem>
);

export default DropdownMenuItem;
